import { pdf } from '@react-pdf/renderer';
import { ArrowLeft, CheckCircle2, Save, Star } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { BrandingTab } from '@/components/settings/branding-tab';
import { ContentTab } from '@/components/settings/content-tab';
import { FieldsTab } from '@/components/settings/fields-tab';
import { type LayoutSettings, LayoutTab } from '@/components/settings/layout-tab';
import { PreviewToolbar } from '@/components/settings/preview-toolbar';
import { ReactPdfViewer, ZOOM_STEPS } from '@/components/settings/ReactPdfViewer';
import { replaceVariables } from '@/components/settings/sample-data';
import { SettingsTab } from '@/components/settings/settings-tab';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCompany } from '@/contexts/CompanyContext';
import { DEFAULT_AGEING_BANDS, useAgeingBands } from '@/hooks/useAgeingBands';
import { useApi } from '@/hooks/useApi';
import { useCompanyWithProfile } from '@/hooks/useCompanyWithProfile';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { fetchImageAsDataUrl } from '@/hooks/useImageToDataUrl';
import {
  useCreateTemplate,
  useDeleteTemplate,
  useSetDefaultTemplate,
  useTemplateById,
  useUpdateTemplate,
} from '@/hooks/useTemplatesData';
import { mapTemplateDetailApiToUi } from '@/lib/mappers/templates';
import { StatementDocument } from '@/lib/react-pdf/StatementDocument';
import { buildSampleData, buildVariablePlaceholderData, reconcileBuckets } from '@/lib/react-pdf/statement-data';
import { ApiError } from '@/providers/ApiConfigProvider';
import type { AgeingBandConfig } from '@/types/ageing-analysis';
import type { Template } from '@/types/template';
import type { LogoSource, TemplateBuilderConfig } from '@/types/template-builder';

type TabType = 'branding' | 'layout' | 'content' | 'fields' | 'settings';

type TemplateType = 'blank' | 'formal' | 'simple' | 'detailed';

type PageSize = 'a4' | 'a5' | 'letter';
type Orientation = 'portrait' | 'landscape';
type LogoSize = 'small' | 'medium' | 'large';

interface BuilderState {
  templateName: string;
  templateType: TemplateType;
  companyName: string;
  description: string;
  isDefault: boolean;
  hasUnsavedChanges: boolean;
}

// --- Default settings factories (outside component to reduce complexity) ---

function getDefaultLayoutSettings(templateType: TemplateType): LayoutSettings {
  const base: LayoutSettings = {
    pageSize: 'a4',
    orientation: 'portrait',
    margins: 'normal',
    headerLayout: {
      left: ['empty'],
      center: ['empty'],
      right: ['empty'],
      showDivider: false,
      dividerThickness: '1',
      dividerColor: '#e5e7eb',
      repeatOnAllPages: false,
    },
    footer: {
      enabled: false,
      alignment: 'left' as const,
      showCompanyContact: false,
      showCompanyReg: false,
      showVatNumber: false,
      showDivider: false,
      repeatOnAllPages: false,
    },
    pageNumbers: {
      enabled: false,
      position: 'bottom-center',
      format: 'page-of',
    },
  };

  if (templateType === 'blank') return base;

  if (templateType === 'formal') {
    return {
      ...base,
      headerLayout: {
        left: ['logo'],
        center: ['empty'],
        right: ['company-name'],
        showDivider: true,
        dividerThickness: '1',
        dividerColor: '#e5e7eb',
        repeatOnAllPages: true,
      },
      footer: {
        enabled: true,
        alignment: 'left' as const,
        showCompanyContact: true,
        showCompanyReg: true,
        showVatNumber: true,
        showDivider: true,
        dividerThickness: '1',
        dividerColor: '#e5e7eb',
        repeatOnAllPages: true,
      },
      pageNumbers: {
        enabled: true,
        position: 'bottom-center',
        format: 'page-of',
      },
    };
  }

  if (templateType === 'simple') {
    return {
      ...base,
      headerLayout: {
        left: ['logo'],
        center: ['empty'],
        right: ['empty'],
        showDivider: false,
        dividerThickness: '1',
        dividerColor: '#e5e7eb',
        repeatOnAllPages: true,
      },
    };
  }

  // detailed
  return {
    ...base,
    headerLayout: {
      left: ['logo'],
      center: ['company-address'],
      right: ['company-name'],
      showDivider: true,
      dividerThickness: '1',
      dividerColor: '#e5e7eb',
      repeatOnAllPages: true,
    },
    footer: {
      enabled: true,
      alignment: 'left' as const,
      showCompanyContact: true,
      showCompanyReg: true,
      showVatNumber: true,
      showDivider: true,
      dividerThickness: '1',
      dividerColor: '#e5e7eb',
      repeatOnAllPages: true,
    },
    pageNumbers: {
      enabled: true,
      position: 'bottom-center',
      format: 'page-of',
    },
  };
}

function getDefaultContentSettings(templateType: TemplateType) {
  const isNotBlank = templateType !== 'blank';
  const isDetailed = templateType === 'detailed';
  const isFormalOrDetailed = templateType === 'formal' || isDetailed;

  return {
    documentTitle: 'Statement of Account',
    customerAddress: {
      enabled: isNotBlank,
      content:
        '<<customerName>>\n<<customerAddressLine>>\n<<customerCity>>\n<<customerCounty>>\n<<customerPostcode>>\n<<customerCountry>>',
    },
    referenceBlock: {
      enabled: isNotBlank,
      items: {
        accountNumber: isNotBlank,
        currencyCode: isNotBlank,
        paymentTerms: isNotBlank,
        statementDate: isNotBlank,
      },
    },
    introText: {
      enabled: isDetailed,
      content: isDetailed
        ? 'Please find below your statement of account as of <<statementDate>>. If you have any queries please contact us at <<companyEmail>>.'
        : '',
    },
    paymentInstructions: {
      enabled: isFormalOrDetailed,
      content: isFormalOrDetailed
        ? 'Please make payment to <<companyBankName>>, Sort Code <<sortCode>>, Account Number <<bankAccountNumber>>. Please use your account number <<accountNumber>> as the payment reference.'
        : '',
    },
    closingText: {
      enabled: isDetailed,
      content: isDetailed
        ? 'Thank you for your continued business. If you have any questions regarding this statement please contact us at <<companyEmail>> or <<companyPhone>>.'
        : '',
    },
  };
}

type ContentSettings = ReturnType<typeof getDefaultContentSettings>;

function getDefaultFieldsSettings(templateType: TemplateType, ageingBands?: AgeingBandConfig[]) {
  const bands = ageingBands ?? DEFAULT_AGEING_BANDS;
  const allColumns = [
    {
      id: 'invoice_no',
      name: 'Reference Number',
      variable: '<<referenceNumber>>',
      enabled: true,
    },
    {
      id: 'invoice_date',
      name: 'Transaction Date',
      variable: '<<transactionDate>>',
      enabled: true,
    },
    {
      id: 'due_date',
      name: 'Due Date',
      variable: '<<dueDate>>',
      enabled: true,
    },
    {
      id: 'description',
      name: 'Description',
      variable: '<<transactionDescription>>',
      enabled: true,
    },
    {
      id: 'invoice_amount',
      name: 'Gross Amount',
      variable: '<<grossAmount>>',
      enabled: true,
    },
    {
      id: 'payments_received',
      name: 'Payments Received',
      variable: '<<paymentsReceived>>',
      enabled: true,
    },
    {
      id: 'balance_outstanding',
      name: 'Outstanding Amount',
      variable: '<<outstandingAmount>>',
      enabled: true,
    },
    {
      id: 'days_overdue',
      name: 'Days Overdue',
      variable: '<<daysOverdue>>',
      enabled: true,
    },
    {
      id: 'po_number',
      name: 'Order Number',
      variable: '<<orderNumber>>',
      enabled: false,
    },
    {
      id: 'transaction_type',
      name: 'Type',
      variable: '<<transactionType>>',
      enabled: false,
    },
  ];

  const COLUMN_SETS: Record<string, string[]> = {
    simple: ['invoice_no', 'invoice_date', 'due_date', 'invoice_amount', 'balance_outstanding', 'days_overdue'],
    blank: ['invoice_no', 'invoice_date', 'due_date', 'invoice_amount', 'balance_outstanding'],
  };

  let columnsForType = allColumns;
  if (COLUMN_SETS[templateType]) {
    columnsForType = allColumns.map((col) => ({
      ...col,
      enabled: COLUMN_SETS[templateType].includes(col.id),
    }));
  } else if (templateType === 'detailed') {
    columnsForType = allColumns.map((col) => ({
      ...col,
      enabled: col.id !== 'transaction_type',
    }));
  }

  const isDetailed = templateType === 'detailed';
  const isFormalOrDetailed = templateType === 'formal' || isDetailed;
  const isNotSimple = templateType !== 'simple';

  return {
    accountSummary: {
      enabled: isFormalOrDetailed,
      items: {
        totalOutstanding: isNotSimple,
        totalOverdue: isNotSimple,
        totalNotYetDue: isNotSimple,
        creditLimit: isDetailed,
        availableCredit: isDetailed,
        lastPaymentAmount: isDetailed,
        lastPaymentDate: isDetailed,
      },
    },
    transactionTable: {
      columns: columnsForType,
      displayOptions: {
        showBorders: templateType !== 'simple',
        zebraStripe: false,
        highlightOverdue: true,
        boldOverdue: true,
      },
      sortBy: 'oldest-first',
    },
    totals: {
      items: {
        totalInvoiced: isDetailed,
        totalOutstanding: true,
        totalOverdue: isFormalOrDetailed,
        totalNotYetDue: isFormalOrDetailed,
      },
      alignment: 'right',
    },
    agingSummary: {
      enabled: isDetailed,
      buckets: Object.fromEntries(bands.map((band) => [String(band.sequence), isDetailed || isNotSimple])),
      displayStyle: 'table' as string,
      highlightOverdue: isNotSimple && templateType !== 'blank',
    },
  };
}

type FieldsSettings = ReturnType<typeof getDefaultFieldsSettings>;

// --- Top bar ---

function BuilderTopBar({
  builderState,
  isEditingName,
  isLoading,
  isSaving,
  isEditMode,
  showSavedIndicator,
  onBack,
  onNameChange,
  onEditNameToggle,
  onSetAsDefault,
  onSaveDraft,
  onSaveTemplate,
}: Readonly<{
  builderState: BuilderState;
  isEditingName: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isEditMode: boolean;
  showSavedIndicator: boolean;
  onBack: () => void;
  onNameChange: (name: string) => void;
  onEditNameToggle: (editing: boolean) => void;
  onSetAsDefault: () => void;
  onSaveDraft: () => void;
  onSaveTemplate: () => void;
}>) {
  return (
    <div className="h-16 border-b bg-white flex items-center justify-between px-6 flex-shrink-0 sticky top-0 z-50">
      <div className="flex items-center gap-4">
        <TemplateNameSection
          builderState={builderState}
          isEditingName={isEditingName}
          onNameChange={onNameChange}
          onEditNameToggle={onEditNameToggle}
        />
      </div>
      <TopBarActions
        builderState={builderState}
        isLoading={isLoading}
        isSaving={isSaving}
        isEditMode={isEditMode}
        showSavedIndicator={showSavedIndicator}
        onBack={onBack}
        onSetAsDefault={onSetAsDefault}
        onSaveDraft={onSaveDraft}
        onSaveTemplate={onSaveTemplate}
      />
    </div>
  );
}

function TemplateNameSection({
  builderState,
  isEditingName,
  onNameChange,
  onEditNameToggle,
}: Readonly<{
  builderState: BuilderState;
  isEditingName: boolean;
  onNameChange: (name: string) => void;
  onEditNameToggle: (editing: boolean) => void;
}>) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2">
        {isEditingName ? (
          <Input
            value={builderState.templateName}
            onChange={(e) => onNameChange(e.target.value)}
            onBlur={() => onEditNameToggle(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onEditNameToggle(false);
            }}
            className="h-8 w-64"
            autoFocus
          />
        ) : (
          <button
            type="button"
            className="text-lg font-semibold cursor-pointer hover:text-primary bg-transparent border-none p-0 text-left"
            onClick={() => onEditNameToggle(true)}
          >
            {builderState.templateName}
          </button>
        )}
        <Badge variant="secondary" className="bg-teal-50 text-teal-700 border-teal-200">
          Customer Statement
        </Badge>
        {builderState.hasUnsavedChanges && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <div className="h-2 w-2 rounded-full bg-muted-foreground" />
            <span>Unsaved changes</span>
          </div>
        )}
      </div>
    </div>
  );
}

function TopBarActions({
  builderState,
  isLoading,
  isSaving,
  isEditMode,
  showSavedIndicator,
  onBack,
  onSetAsDefault,
  onSaveDraft,
  onSaveTemplate,
}: Readonly<{
  builderState: BuilderState;
  isLoading: boolean;
  isSaving: boolean;
  isEditMode: boolean;
  showSavedIndicator: boolean;
  onBack: () => void;
  onSetAsDefault: () => void;
  onSaveDraft: () => void;
  onSaveTemplate: () => void;
}>) {
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={onBack}>
        Cancel
      </Button>
      <DefaultBadge isDefault={builderState.isDefault} isLoading={isLoading} onSetAsDefault={onSetAsDefault} />
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onSaveDraft} disabled={isLoading || isSaving}>
          <Save className="mr-2 h-4 w-4" />
          Save Draft
        </Button>
        {showSavedIndicator && (
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Draft saved
          </span>
        )}
      </div>
      <Button size="sm" onClick={onSaveTemplate} disabled={isLoading || isSaving}>
        {isEditMode ? 'Save Changes' : 'Save Template'}
      </Button>
    </div>
  );
}

function DefaultBadge({
  isDefault,
  isLoading,
  onSetAsDefault,
}: Readonly<{
  isDefault: boolean;
  isLoading: boolean;
  onSetAsDefault: () => void;
}>) {
  if (isDefault) {
    return (
      <Button variant="secondary" size="sm" disabled className="bg-teal-50 text-teal-700 border-teal-200">
        <Star className="mr-2 h-4 w-4 fill-current" />
        Default
      </Button>
    );
  }
  return (
    <Button variant="outline" size="sm" onClick={onSetAsDefault} disabled={isLoading}>
      <Star className="mr-2 h-4 w-4" />
      Set as Default
    </Button>
  );
}

// --- Left panel ---

function BuilderLeftPanel({
  activeTab,
  onTabChange,
  isLoading,
  children,
}: Readonly<{
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  isLoading: boolean;
  children: ReactNode;
}>) {
  return (
    <div className="w-96 border-r bg-white flex flex-col flex-shrink-0 overflow-hidden">
      <div className="border-b">
        <div className="flex overflow-x-auto">
          {(['branding', 'layout', 'content', 'fields', 'settings'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className={`flex-1 px-3 py-3 text-sm font-medium capitalize transition-colors whitespace-nowrap ${activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">{isLoading ? <LoadingPlaceholder /> : children}</div>
    </div>
  );
}

function LoadingPlaceholder() {
  return (
    <div className="space-y-4">
      <div className="h-32 bg-muted animate-pulse rounded" />
      <div className="h-24 bg-muted animate-pulse rounded" />
      <div className="h-40 bg-muted animate-pulse rounded" />
    </div>
  );
}

// --- Small screen fallback ---

function SmallScreenMessage({ onBack }: Readonly<{ onBack: () => void }>) {
  return (
    <div className="flex items-center justify-center min-h-screen p-8">
      <div className="text-center max-w-md">
        <h2 className="text-2xl font-semibold mb-4">Desktop Required</h2>
        <p className="text-muted-foreground mb-6">
          The template builder requires a larger screen. Please use a desktop or laptop to edit templates.
        </p>
        <Button onClick={onBack} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Templates
        </Button>
      </div>
    </div>
  );
}

// --- Initial state computation ---

export interface PDFBuilderLocationState {
  templateName?: string;
  templateType?: TemplateType;
  description?: string;
  template?: Record<string, unknown>;
  mode?: 'edit' | 'create';
  duplicateFromId?: string;
}

interface InitialValues {
  isEditMode: boolean;
  shouldLoadTemplateData: boolean;
  builderState: BuilderState;
  logo: string | null;
  logoSize: LogoSize;
  logoSource: LogoSource;
  primaryColor: string;
  documentFont: string;
  layoutSettings: LayoutSettings;
  contentSettings: ContentSettings;
  fieldsSettings: FieldsSettings;
  settingsTabSettings: SettingsTabSettingsType;
}

interface SettingsTabSettingsType {
  isDefault: boolean;
  dateRange: {
    type: string;
    customFrom: Date | undefined;
    customTo: Date | undefined;
  };
  sendSettings: {
    overridePaperSize: boolean;
    paperSize: 'a4' | 'a5' | 'letter';
    filenameFormat: string;
  };
  templateInfo: {
    type: string;
    company: string;
    created?: Date;
    lastModified?: Date;
    startingPoint: string;
  };
}

function inferLogoSource(
  pdfSettings: Record<string, unknown> | undefined,
  companyLogoUrl: string | undefined,
): LogoSource {
  const saved = pdfSettings?.logoSource as LogoSource | undefined;
  if (saved) return saved;
  const storedUrl = (pdfSettings?.logo as Record<string, unknown>)?.url as string | undefined;
  if (!storedUrl) return companyLogoUrl ? 'company' : 'none';
  if (storedUrl.startsWith('data:')) return 'custom';
  return 'company';
}

function computeBrandingValues(
  shouldLoad: boolean,
  pdfSettings: Record<string, unknown> | undefined,
  styling: Record<string, unknown> | undefined,
  companyLogoUrl: string | undefined,
) {
  const logoSource: LogoSource = shouldLoad
    ? inferLogoSource(pdfSettings, companyLogoUrl)
    : companyLogoUrl
      ? 'company'
      : 'none';

  let logo: string | null;
  if (logoSource === 'company') {
    logo = companyLogoUrl || null;
  } else if (logoSource === 'custom') {
    logo = shouldLoad ? ((pdfSettings?.logo as Record<string, unknown>)?.url as string) || null : null;
  } else {
    logo = null;
  }

  const logoSize: LogoSize = shouldLoad && pdfSettings?.logoSize ? (pdfSettings.logoSize as LogoSize) : 'medium';
  const primaryColor = shouldLoad && styling?.primaryColor ? (styling.primaryColor as string) : '#1a5c5c';
  const documentFont = shouldLoad && styling?.fontFamily ? (styling.fontFamily as string) : 'Arial';
  return { logo, logoSize, logoSource, primaryColor, documentFont };
}

function computeTabSettings(
  shouldLoad: boolean,
  editTemplate: Record<string, unknown> | undefined,
  settingsFromTpl: Record<string, unknown> | undefined,
  companyName: string,
  templateType: TemplateType,
  isDuplicateMode: boolean,
): SettingsTabSettingsType {
  const defaultDateRange = {
    type: 'last-12-months',
    customFrom: undefined as Date | undefined,
    customTo: undefined as Date | undefined,
  };
  const defaultSendSettings = {
    overridePaperSize: false,
    paperSize: 'a4' as const,
    filenameFormat: 'Statement_<<accountNumber>>_<<statementDate>>',
  };
  return {
    isDefault: shouldLoad && !isDuplicateMode ? (editTemplate?.isDefault as boolean) : false,
    dateRange:
      shouldLoad && settingsFromTpl?.dateRange
        ? (settingsFromTpl.dateRange as SettingsTabSettingsType['dateRange'])
        : defaultDateRange,
    sendSettings:
      shouldLoad && settingsFromTpl?.sendSettings
        ? (settingsFromTpl.sendSettings as SettingsTabSettingsType['sendSettings'])
        : defaultSendSettings,
    templateInfo: {
      type: 'Customer Statement',
      company: companyName,
      created: shouldLoad && editTemplate?.createdAt ? new Date(editTemplate.createdAt as string) : undefined,
      lastModified:
        shouldLoad && editTemplate?.lastModified ? new Date(editTemplate.lastModified as string) : undefined,
      startingPoint: templateType.charAt(0).toUpperCase() + templateType.slice(1),
    },
  };
}

function computeInitialValues(
  locationState: PDFBuilderLocationState | null,
  companyLogoUrl: string | undefined,
  selectedCompanyName: string | undefined,
  ageingBands?: AgeingBandConfig[],
): InitialValues {
  const isEditMode = locationState?.mode === 'edit';
  const isDuplicateMode = locationState?.mode === 'create' && !!locationState?.template;
  const shouldLoad = isEditMode || isDuplicateMode;
  const editTemplate = locationState?.template;
  const pdfSettings = editTemplate?.pdfSettings as Record<string, unknown> | undefined;
  const styling = pdfSettings?.styling as Record<string, unknown> | undefined;
  const settingsFromTpl = pdfSettings?.settingsTabSettings as Record<string, unknown> | undefined;

  const templateType: TemplateType = shouldLoad
    ? (pdfSettings?.templateType as TemplateType) || 'detailed'
    : locationState?.templateType || 'detailed';
  const companyName = shouldLoad
    ? (pdfSettings?.companyName as string) || selectedCompanyName || ''
    : selectedCompanyName || '';

  const builderState: BuilderState = {
    templateName: shouldLoad ? (editTemplate?.name as string) : locationState?.templateName || 'Untitled Template',
    templateType,
    companyName,
    description: shouldLoad ? (editTemplate?.subject as string) || '' : locationState?.description || '',
    isDefault: shouldLoad && !isDuplicateMode ? (editTemplate?.isDefault as boolean) : false,
    hasUnsavedChanges: false,
  };

  const branding = computeBrandingValues(shouldLoad, pdfSettings, styling, companyLogoUrl);

  const layoutSettings =
    shouldLoad && pdfSettings?.layoutSettings
      ? (pdfSettings.layoutSettings as LayoutSettings)
      : getDefaultLayoutSettings(templateType);
  const defaultAddressContent =
    '<<customerName>>\n<<customerAddressLine>>\n<<customerCity>>\n<<customerCounty>>\n<<customerPostcode>>\n<<customerCountry>>';
  const loadedContentSettings =
    shouldLoad && pdfSettings?.contentSettings
      ? (pdfSettings.contentSettings as ContentSettings)
      : getDefaultContentSettings(templateType);
  const contentSettings = {
    ...loadedContentSettings,
    customerAddress: {
      enabled: loadedContentSettings.customerAddress?.enabled ?? true,
      content: loadedContentSettings.customerAddress?.content ?? defaultAddressContent,
    },
  };
  const bands = ageingBands ?? DEFAULT_AGEING_BANDS;
  const rawFieldsSettings =
    shouldLoad && pdfSettings?.fieldsSettings
      ? (pdfSettings.fieldsSettings as FieldsSettings)
      : getDefaultFieldsSettings(templateType, bands);
  const fieldsSettings =
    shouldLoad && rawFieldsSettings.agingSummary?.buckets
      ? {
          ...rawFieldsSettings,
          agingSummary: {
            ...rawFieldsSettings.agingSummary,
            buckets: reconcileBuckets(rawFieldsSettings.agingSummary.buckets, bands),
          },
        }
      : rawFieldsSettings;

  const settingsTabSettings = computeTabSettings(
    shouldLoad,
    editTemplate,
    settingsFromTpl,
    companyName,
    templateType,
    isDuplicateMode,
  );

  return {
    isEditMode,
    shouldLoadTemplateData: shouldLoad,
    builderState,
    ...branding,
    layoutSettings,
    contentSettings,
    fieldsSettings,
    settingsTabSettings,
  };
}

// --- Main PDFBuilderPage component ---

interface PDFBuilderPageProps {
  inlineMode?: {
    initialState: PDFBuilderLocationState;
    onBack: () => void;
    onSaved: (savedTemplate: Template, mode: 'create' | 'edit') => void;
    onDuplicate?: (state: PDFBuilderLocationState) => void;
  };
}

export function PDFBuilderPage({ inlineMode }: PDFBuilderPageProps = {}) {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const routeParams = useParams();
  const location = useLocation();
  const { selectedCompany } = useCompany();
  const { data: ageingBands } = useAgeingBands();

  // In inline mode, track the template ID locally (starts as 'new', updated after first save)
  const [inlineId, setInlineId] = useState<string>('new');
  const id = inlineMode ? inlineId : routeParams.id;

  const { apiBaseUrl, getHeaders } = useApi();
  const { data: companyWithProfile, isLoading: isCompanyLoading } = useCompanyWithProfile(selectedCompany?.id);
  const companyLogoUrl = companyWithProfile?.details?.logoUrl ?? selectedCompany?.logoUrl;
  const locationState = inlineMode ? inlineMode.initialState : (location.state as PDFBuilderLocationState | null);
  const initial = computeInitialValues(locationState, companyLogoUrl, selectedCompany?.name, ageingBands ?? undefined);

  // Fetch full template detail (including pdfSettings) when editing an existing template
  const shouldFetchDetail = initial.isEditMode && !!id && id !== 'new';
  const { data: fetchedTemplate } = useTemplateById(shouldFetchDetail ? id : null);
  const hasAppliedFetchedTemplate = useRef(false);
  const hasInitializedNewTemplate = useRef(false);

  const [activeTab, setActiveTab] = useState<TabType>('branding');
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const [showSampleData, setShowSampleData] = useState(false);

  const [zoomIndex, setZoomIndex] = useState(4); // default 100%

  const [builderState, setBuilderState] = useState<BuilderState>(initial.builderState);
  const [currentLogo, setCurrentLogo] = useState<string | null>(initial.logo);
  const [logoSize, setLogoSize] = useState<LogoSize>(initial.logoSize);
  const [logoSource, setLogoSource] = useState<LogoSource>(initial.logoSource);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  const [primaryColor, setPrimaryColor] = useState<string>(initial.primaryColor);
  const [documentFont, setDocumentFont] = useState<string>(initial.documentFont);

  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>(initial.layoutSettings);
  const [contentSettings, setContentSettings] = useState(initial.contentSettings);
  const [fieldsSettings, setFieldsSettings] = useState(initial.fieldsSettings);
  const [settingsTabSettings, setSettingsTabSettings] = useState(initial.settingsTabSettings);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [savedTemplateId, setSavedTemplateId] = useState<string | null>(null);

  // --- API mutations ---

  const isEditMode = initial.isEditMode;
  const effectiveId = savedTemplateId ?? (isEditMode && id && id !== 'new' ? id : null);
  const createMutation = useCreateTemplate({ forceLive: true });
  const updateMutation = useUpdateTemplate(effectiveId ?? '', {
    forceLive: true,
  });
  const deleteMutation = useDeleteTemplate();
  const setDefaultMutation = useSetDefaultTemplate();

  const buildTemplateData = useCallback((): Template => {
    return {
      id: effectiveId ?? '',
      name: builderState.templateName,
      type: 'statement',
      subject: builderState.description || '',
      content: contentSettings.documentTitle ?? '',
      isDefault: builderState.isDefault,
      variables: [],
      lastModified: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      createdBy: '',
      pdfSettings: {
        templateType: builderState.templateType,
        companyName: builderState.companyName,
        logoSize,
        logoSource,
        styling: {
          fontFamily: documentFont,
          fontSize: 12,
          primaryColor,
          lineSpacing: 1.5,
        },
        logo:
          logoSource === 'custom' && currentLogo
            ? {
                url: currentLogo,
                position: 'top-left',
                width: 150,
                height: 50,
              }
            : undefined,
        layoutSettings,
        contentSettings,
        fieldsSettings: ageingBands
          ? {
              ...fieldsSettings,
              agingSummary: {
                ...fieldsSettings.agingSummary,
                buckets: Object.fromEntries(
                  Object.entries(fieldsSettings.agingSummary.buckets).filter(([key]) =>
                    ageingBands.some((b) => String(b.sequence) === key),
                  ),
                ),
              },
            }
          : fieldsSettings,
        settingsTabSettings,
      },
    };
  }, [
    effectiveId,
    builderState.templateName,
    builderState.isDefault,
    builderState.templateType,
    builderState.companyName,
    builderState.description,
    logoSize,
    logoSource,
    documentFont,
    primaryColor,
    currentLogo,
    layoutSettings,
    contentSettings,
    fieldsSettings,
    settingsTabSettings,
    ageingBands,
  ]);

  // Apply fetched template detail (with pdfSettings) to all state once loaded, then fetch logo.
  // Also wait for company data so companyLogoUrl is available for inferLogoSource.
  useEffect(() => {
    if (!fetchedTemplate) return;
    if (!selectedCompany?.id || isCompanyLoading) return;
    if (hasAppliedFetchedTemplate.current) return;
    hasAppliedFetchedTemplate.current = true;

    const fetchedState: PDFBuilderLocationState = {
      template: fetchedTemplate as unknown as Record<string, unknown>,
      mode: 'edit',
    };
    const recomputed = computeInitialValues(
      fetchedState,
      companyLogoUrl,
      selectedCompany?.name,
      ageingBands ?? undefined,
    );

    setBuilderState(recomputed.builderState);
    setCurrentLogo(recomputed.logo);
    setLogoSize(recomputed.logoSize);
    setLogoSource(recomputed.logoSource);
    setPrimaryColor(recomputed.primaryColor);
    setDocumentFont(recomputed.documentFont);
    setLayoutSettings(recomputed.layoutSettings);
    setContentSettings(recomputed.contentSettings);
    setFieldsSettings(recomputed.fieldsSettings);
    setSettingsTabSettings(recomputed.settingsTabSettings);

    const rawLogoUrl =
      recomputed.logoSource === 'company' && selectedCompany?.id
        ? `${apiBaseUrl}/api/v1/company/${selectedCompany.id}/logo`
        : recomputed.logoSource === 'custom'
          ? (recomputed.logo ?? null)
          : null;

    void (async () => {
      try {
        const headers = await getHeaders();
        const dataUrl = await fetchImageAsDataUrl(rawLogoUrl, { headers });
        setLogoDataUrl(dataUrl);
      } catch {
        setLogoDataUrl(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    fetchedTemplate,
    isCompanyLoading,
    companyLogoUrl,
    selectedCompany?.name,
    selectedCompany?.id,
    apiBaseUrl,
    getHeaders,
    ageingBands,
  ]);

  // For new templates: wait for company data to load, fetch logo data URL, then clear loading
  useEffect(() => {
    if (shouldFetchDetail) return;
    if (!selectedCompany?.id || isCompanyLoading) return;
    if (hasInitializedNewTemplate.current) return;
    hasInitializedNewTemplate.current = true;

    if (companyLogoUrl && !builderState.hasUnsavedChanges) {
      setLogoSource('company');
      setCurrentLogo(companyLogoUrl);
    }

    const rawLogoUrl =
      companyLogoUrl && selectedCompany?.id ? `${apiBaseUrl}/api/v1/company/${selectedCompany.id}/logo` : null;

    void (async () => {
      try {
        const headers = await getHeaders();
        const dataUrl = await fetchImageAsDataUrl(rawLogoUrl, { headers });
        setLogoDataUrl(dataUrl);
      } catch {
        setLogoDataUrl(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    shouldFetchDetail,
    isCompanyLoading,
    companyLogoUrl,
    selectedCompany?.id,
    apiBaseUrl,
    getHeaders,
    builderState.hasUnsavedChanges,
  ]);

  // --- react-pdf config & data ---

  const templateBuilderConfig = useMemo<TemplateBuilderConfig>(
    () => ({
      layoutSettings,
      contentSettings,
      fieldsSettings,
      primaryColor,
      documentFont,
      logoUrl: logoDataUrl,
      logoSize,
      logoSource,
      companyName: selectedCompany?.name ?? builderState.companyName,
      companyEmail: selectedCompany?.email ?? companyWithProfile?.details?.email ?? undefined,
      companyPhone: selectedCompany?.phone ?? companyWithProfile?.details?.phone ?? undefined,
      companyReg:
        selectedCompany?.companyRegistrationNumber ??
        companyWithProfile?.details?.companyRegistrationNumber ??
        undefined,
      companyVat: selectedCompany?.vatRegistrationNumber ?? companyWithProfile?.details?.vatNumber ?? undefined,
      companyAddress: companyWithProfile?.details?.addressLine ?? undefined,
      companyCountry: companyWithProfile?.details?.country ?? undefined,
      companyBankName: companyWithProfile?.bankDetails?.[0]?.accountName ?? undefined,
      sortCode: companyWithProfile?.bankDetails?.[0]?.sortCode ?? undefined,
      bankAccountNumber: companyWithProfile?.bankDetails?.[0]?.accountNumber ?? undefined,
      ibanNumber: companyWithProfile?.bankDetails?.[0]?.ibanNumber ?? undefined,
      swiftCode: companyWithProfile?.bankDetails?.[0]?.swiftBicCode ?? undefined,
    }),
    [
      layoutSettings,
      contentSettings,
      fieldsSettings,
      primaryColor,
      documentFont,
      logoDataUrl,
      logoSize,
      logoSource,
      builderState.companyName,
      selectedCompany,
      companyWithProfile,
    ],
  );

  const debouncedConfig = useDebouncedValue(templateBuilderConfig, 500);

  const sampleData = useMemo(
    () => buildSampleData(debouncedConfig, ageingBands ?? undefined),
    [debouncedConfig, ageingBands],
  );
  const varData = useMemo(
    () => buildVariablePlaceholderData(debouncedConfig, ageingBands ?? undefined),
    [debouncedConfig, ageingBands],
  );

  // --- Handlers ---

  const handleBack = () => {
    setShowExitDialog(true);
  };
  const handleConfirmExit = () => {
    setShowExitDialog(false);
    if (inlineMode) {
      inlineMode.onBack();
    } else {
      navigate('/settings/templates');
    }
  };
  const handleNameChange = (newName: string) => {
    setBuilderState((prev) => ({
      ...prev,
      templateName: newName || 'Untitled Template',
      hasUnsavedChanges: true,
    }));
  };

  const handleSetAsDefault = () => {
    if (builderState.isDefault) return;

    if (isEditMode && id && id !== 'new') {
      setDefaultMutation.mutate(id, {
        onSuccess: () => {
          setBuilderState((prev) => ({
            ...prev,
            isDefault: true,
          }));
          toast.success('Default template set', {
            description: 'This template is now the default Customer Statement.',
          });
        },
        onError: (error) => {
          const message = error instanceof ApiError ? error.message : 'Failed to set template as default.';
          toast.error(message);
        },
      });
    } else {
      setBuilderState((prev) => ({
        ...prev,
        isDefault: true,
        hasUnsavedChanges: true,
      }));
      toast.success('Default template set', {
        description: 'This template will be set as default when saved.',
      });
    }
  };

  const handleSaveDraft = () => {
    setIsSaving(true);
    const templateData = buildTemplateData();
    const mutation = effectiveId ? updateMutation : createMutation;

    mutation.mutate(templateData, {
      onSuccess: (response) => {
        setBuilderState((prev) => ({ ...prev, hasUnsavedChanges: false }));
        setIsSaving(false);
        setShowSavedIndicator(true);
        setTimeout(() => setShowSavedIndicator(false), 3000);

        if (!isEditMode && response?.id) {
          const saved = mapTemplateDetailApiToUi(response);
          setSavedTemplateId(String(saved.id));
          if (inlineMode) {
            setInlineId(saved.id);
          } else {
            navigate(`/settings/templates/builder/${saved.id}`, {
              state: { template: saved, mode: 'edit' },
              replace: true,
            });
          }
        }
      },
      onError: (error) => {
        setIsSaving(false);
        const message = error instanceof ApiError ? error.message : 'Failed to save draft.';
        toast.error(message);
      },
    });
  };

  const handleSaveTemplate = () => {
    setIsSaving(true);
    const templateData = buildTemplateData();
    const mutation = effectiveId ? updateMutation : createMutation;

    mutation.mutate(templateData, {
      onSuccess: (response) => {
        const saved = mapTemplateDetailApiToUi(response);
        toast.success(isEditMode ? 'Changes saved successfully' : 'Template saved successfully', {
          description: isEditMode ? 'Your template has been updated.' : 'Your template has been published.',
        });
        if (inlineMode) {
          inlineMode.onSaved(saved, isEditMode ? 'edit' : 'create');
        } else {
          navigate('/settings/templates', { state: { savedTemplate: saved } });
        }
      },
      onError: (error) => {
        setIsSaving(false);
        const message = error instanceof ApiError ? error.message : 'Failed to save template.';
        toast.error(message);
      },
    });
  };

  const handleExportPDF = async () => {
    const blob = await pdf(<StatementDocument config={templateBuilderConfig} data={sampleData} />).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const filename =
      replaceVariables(settingsTabSettings.sendSettings.filenameFormat, true).replace(/\s+/g, '_') + '.pdf';
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLogoChange = (logoUrl: string | null, source: LogoSource) => {
    setLogoSource(source);
    if (source === 'company') {
      setCurrentLogo(companyLogoUrl || null);
      const rawUrl = selectedCompany?.id ? `${apiBaseUrl}/api/v1/company/${selectedCompany.id}/logo` : null;
      void (async () => {
        try {
          const headers = await getHeaders();
          const dataUrl = await fetchImageAsDataUrl(rawUrl, { headers });
          setLogoDataUrl(dataUrl);
        } catch {
          setLogoDataUrl(null);
        }
      })();
    } else if (source === 'custom') {
      setCurrentLogo(logoUrl);
      setLogoDataUrl(logoUrl);
    } else {
      setCurrentLogo(null);
      setLogoDataUrl(null);
    }
    setBuilderState((prev) => ({ ...prev, hasUnsavedChanges: true }));
  };
  const handleSizeChange = (size: LogoSize) => {
    setLogoSize(size);
    setBuilderState((prev) => ({ ...prev, hasUnsavedChanges: true }));
  };
  const handleContentChange = (newSettings: typeof contentSettings) => {
    setContentSettings(newSettings);
    setBuilderState((prev) => ({ ...prev, hasUnsavedChanges: true }));
  };
  const handleLayoutChange = (settings: LayoutSettings) => {
    setLayoutSettings(settings);
    setBuilderState((prev) => ({ ...prev, hasUnsavedChanges: true }));
  };
  const handleFieldsChange = (newSettings: typeof fieldsSettings) => {
    setFieldsSettings(newSettings);
    setBuilderState((prev) => ({ ...prev, hasUnsavedChanges: true }));
  };
  const handleSettingsChange = (newSettings: typeof settingsTabSettings) => {
    setSettingsTabSettings(newSettings);
    setBuilderState((prev) => ({
      ...prev,
      isDefault: newSettings.isDefault,
      hasUnsavedChanges: true,
    }));
  };
  const handleDuplicateTemplate = () => {
    const templateData = buildTemplateData();
    const duplicateState: PDFBuilderLocationState = {
      template: { ...templateData, name: `${templateData.name} (COPY)` },
      mode: 'create',
      duplicateFromId: id,
    };
    if (inlineMode?.onDuplicate) {
      inlineMode.onDuplicate(duplicateState);
    } else if (inlineMode) {
      inlineMode.onBack();
    } else {
      navigate('/settings/templates/builder/new', {
        state: duplicateState,
      });
    }
  };
  const handleDeleteTemplate = () => {
    setShowDeleteDialog(true);
  };
  const confirmDeleteTemplate = () => {
    if (!id || id === 'new') return;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        toast.success('Template deleted');
        if (inlineMode) {
          inlineMode.onBack();
        } else {
          navigate('/settings/templates');
        }
      },
      onError: (error) => {
        setShowDeleteDialog(false);
        const message = error instanceof ApiError ? error.message : 'Failed to delete template.';
        toast.error(message);
      },
    });
  };

  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const checkScreenSize = () => setIsSmallScreen(window.innerWidth < 1024);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  if (isSmallScreen) {
    return (
      <SmallScreenMessage
        onBack={() => {
          if (inlineMode) {
            inlineMode.onBack();
          } else {
            navigate('/settings/templates');
          }
        }}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <BuilderTopBar
        builderState={builderState}
        isEditingName={isEditingName}
        isLoading={isLoading}
        isSaving={isSaving}
        isEditMode={initial.isEditMode}
        showSavedIndicator={showSavedIndicator}
        onBack={handleBack}
        onNameChange={handleNameChange}
        onEditNameToggle={setIsEditingName}
        onSetAsDefault={handleSetAsDefault}
        onSaveDraft={handleSaveDraft}
        onSaveTemplate={handleSaveTemplate}
      />

      <div className="flex flex-1 overflow-hidden">
        <BuilderLeftPanel activeTab={activeTab} onTabChange={setActiveTab} isLoading={isLoading}>
          {activeTab === 'branding' && (
            <BrandingTab
              companyName={builderState.companyName}
              companyLogoUrl={companyLogoUrl}
              currentLogo={currentLogo}
              logoSource={logoSource}
              onLogoChange={handleLogoChange}
              onSizeChange={handleSizeChange}
              logoSize={logoSize}
              primaryColor={primaryColor}
              documentFont={documentFont}
              onPrimaryColorChange={setPrimaryColor}
              onDocumentFontChange={setDocumentFont}
            />
          )}
          {activeTab === 'layout' && <LayoutTab settings={layoutSettings} onChange={handleLayoutChange} />}
          {activeTab === 'content' && (
            <ContentTab
              settings={contentSettings}
              onChange={handleContentChange}
              templateType={builderState.templateType}
            />
          )}
          {activeTab === 'fields' && (
            <FieldsTab settings={fieldsSettings} onChange={handleFieldsChange} ageingBands={ageingBands ?? undefined} />
          )}
          {activeTab === 'settings' && (
            <SettingsTab
              settings={settingsTabSettings}
              isNewTemplate={id === 'new'}
              otherDefaultTemplate={undefined}
              onSettingsChange={handleSettingsChange}
              onDuplicate={handleDuplicateTemplate}
              onDelete={handleDeleteTemplate}
            />
          )}
        </BuilderLeftPanel>

        <div className="flex-1 flex flex-col overflow-hidden">
          <PreviewToolbar
            showSampleData={showSampleData}
            onToggleSampleData={setShowSampleData}
            hasUnsavedChanges={builderState.hasUnsavedChanges}
            onExportPDF={handleExportPDF}
            zoom={ZOOM_STEPS[zoomIndex]}
            canZoomOut={zoomIndex > 0}
            canZoomIn={zoomIndex < ZOOM_STEPS.length - 1}
            onZoomOut={() => setZoomIndex((i) => i - 1)}
            onZoomIn={() => setZoomIndex((i) => i + 1)}
          />
          <div className="flex-1 bg-[#f0f0f0] overflow-hidden relative">
            {isLoading ? (
              <div className="w-full h-full flex items-center justify-center p-12">
                <div className="w-full h-full bg-muted animate-pulse rounded" />
              </div>
            ) : (
              <ReactPdfViewer
                config={debouncedConfig}
                data={showSampleData ? sampleData : varData}
                zoomIndex={zoomIndex}
                className="h-full"
              />
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel? Any unsaved changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmExit}>Discard Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Template Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>&quot;{builderState.templateName}&quot;</strong>?
              {builderState.isDefault && (
                <span className="block mt-2 text-yellow-600">Warning: This is the default statement template.</span>
              )}
              <span className="block mt-2">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTemplate}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
